import { useEffect, useState } from 'react';
import darkLogo from '../assets/images/ata_code_house_logo(black).png';
import lightLogo from '../assets/images/ata_code_house_logo(white).png';
import { productHighlights } from '../demo/shopping/src/data/productHighlights';
import { formatMMK } from '../demo/shopping/src/lib/format';
import {
  createOrder,
  fetchApiStatus,
  fetchOrders,
  fetchProducts,
  postCartItem,
} from '../demo/shopping/src/lib/mockApi';

function ShoppingAppShowcase({ onReady }) {
  return <ShoppingDemoApp onReady={onReady} />;
}

function ShoppingDemoApp({ onReady }) {
  const [theme, setTheme] = usePersistentState('ata-code-house-theme', 'light');
  const [pageStack, setPageStack] = usePersistentState('ata-code-house-demo-stack', ['home']);
  const [prefersLightMotion, setPrefersLightMotion] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsError, setProductsError] = useState('');
  const [apiStatus, setApiStatus] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [toast, setToast] = useState('');
  const [actionError, setActionError] = useState('');
  const [cartBounceKey, setCartBounceKey] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [flyToCart, setFlyToCart] = useState(null);
  const cart = useShoppingCart();
  const currentPage = pageStack[pageStack.length - 1] || 'home';

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 760px), (prefers-reduced-motion: reduce)');
    const syncPreference = () => setPrefersLightMotion(mediaQuery.matches);

    syncPreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncPreference);
      return () => mediaQuery.removeEventListener('change', syncPreference);
    }

    mediaQuery.addListener(syncPreference);
    return () => mediaQuery.removeListener(syncPreference);
  }, []);

  useEffect(() => {
    let active = true;

    async function loadProducts() {
      setProductsLoading(true);
      setProductsError('');

      try {
        const items = await fetchProducts();
        if (!active) {
          return;
        }

        setProducts(
          items.map((item) => ({
            ...item,
            badge: productHighlights[item.id] || null,
          }))
        );
      } catch (error) {
        if (active) {
          setProductsError(error.message || 'Unable to load products.');
        }
      } finally {
        if (active) {
          setProductsLoading(false);
          window.requestAnimationFrame(() => {
            onReady?.();
          });
        }
      }
    }

    loadProducts();
    return () => {
      active = false;
    };
  }, [onReady]);

  useEffect(() => {
    let active = true;

    async function loadStatus() {
      try {
        const status = await fetchApiStatus();
        if (active) {
          setApiStatus(status);
        }
      } catch {
        if (active) {
          setApiStatus(null);
        }
      }
    }

    loadStatus();
    return () => {
      active = false;
    };
  }, [cart.itemCount, cart.orderHistory.length]);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [currentPage]);

  const navigateTo = (nextPage) => {
    setPageStack((current) =>
      current[current.length - 1] === nextPage ? current : [...current, nextPage]
    );
  };

  const navigateBack = () => {
    setPageStack((current) => (current.length > 1 ? current.slice(0, -1) : current));
  };

  const animateFlyToCart = (product, sourceElement) => {
    if (prefersLightMotion) {
      return;
    }

    const cartIcon = document.querySelector('[data-cart-icon="true"]');
    const sourceRect = sourceElement?.getBoundingClientRect();
    const cartRect = cartIcon?.getBoundingClientRect();

    if (!sourceRect || !cartRect) {
      return;
    }

    const animationId = `${product.id}-${Date.now()}`;
    setFlyToCart({
      id: animationId,
      accent: product.accent,
      orb: product.orb,
      panel: product.panel,
      startX: sourceRect.left + sourceRect.width / 2 - 22,
      startY: sourceRect.top + sourceRect.height / 2 - 22,
      endX: cartRect.left + cartRect.width / 2 - 22,
      endY: cartRect.top + cartRect.height / 2 - 22,
      active: false,
    });

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        setFlyToCart((current) =>
          current && current.id === animationId ? { ...current, active: true } : current
        );
      });
    });

    window.setTimeout(() => {
      setFlyToCart((current) => (current && current.id === animationId ? null : current));
    }, 780);
  };

  const showToast = (title) => {
    const message = `${title} added to cart`;
    setToast(message);
    window.setTimeout(() => {
      setToast((current) => (current === message ? '' : current));
    }, 1200);
  };

  const handleAddToCart = async (product, sourceElement) => {
    setActionError('');
    setCartBounceKey((current) => current + 1);
    showToast(product.title);
    animateFlyToCart(product, sourceElement);

    try {
      await cart.addToCart(product);
    } catch (error) {
      setToast('');
      setActionError(error.message || 'Unable to add item right now.');
    }
  };

  const handlePlaceOrderSuccess = () => {
    setPageStack((current) => [...current, 'history']);
  };

  return (
    <div className={theme === 'dark' ? 'dark' : ''}>
      <div className="relative min-h-screen overflow-hidden bg-[linear-gradient(180deg,#f7f3ec_0%,#f2eee6_45%,#ece7de_100%)] text-stone-900 transition-colors duration-500 dark:bg-[linear-gradient(180deg,#09090b_0%,#0f172a_45%,#020617_100%)] dark:text-stone-100">
        <div className="pointer-events-none absolute inset-0 bg-grain opacity-90 dark:opacity-70" />
        <div className="pointer-events-none absolute -left-24 top-32 h-72 w-72 rounded-full bg-amber-300/20 blur-3xl dark:bg-amber-500/10" />
        <div className="pointer-events-none absolute -right-12 top-24 h-80 w-80 rounded-full bg-cyan-300/20 blur-3xl dark:bg-cyan-400/10" />

        <DemoHeader
          cartBounceKey={cartBounceKey}
          currentPage={currentPage}
          isMenuOpen={isMenuOpen}
          itemCount={cart.itemCount}
          navigateBack={navigateBack}
          navigateTo={navigateTo}
          setIsMenuOpen={setIsMenuOpen}
          setTheme={setTheme}
          theme={theme}
          prefersLightMotion={prefersLightMotion}
        />

        <main
          className="shopping-page-shell relative z-10 mx-auto min-h-[calc(100vh-176px)] w-full max-w-7xl px-4 pb-16 pt-28 sm:px-6 lg:px-8"
          key={currentPage}
        >
          {currentPage === 'home' ? (
            <DemoHome
              actionError={actionError}
              apiStatus={apiStatus}
              handleAddToCart={handleAddToCart}
              lastAddedId={cart.lastAddedId}
              products={products}
              productsError={productsError}
              productsLoading={productsLoading}
              setSelectedProduct={setSelectedProduct}
              isAddingToCart={cart.isAddingToCart}
            />
          ) : null}

          {currentPage === 'cart' ? (
            <DemoCart
              cartItems={cart.cartItems}
              navigateTo={navigateTo}
              removingIds={cart.removingIds}
              removeFromCart={cart.removeFromCart}
              totalPrice={cart.totalPrice}
              updateQuantity={cart.updateQuantity}
            />
          ) : null}

          {currentPage === 'checkout' ? (
            <DemoCheckout
              cartItems={cart.cartItems}
              isPlacingOrder={cart.isPlacingOrder}
              navigateTo={handlePlaceOrderSuccess}
              placeOrder={cart.placeOrder}
              totalPrice={cart.totalPrice}
            />
          ) : null}

          {currentPage === 'history' ? (
            <DemoHistory
              historyError={cart.historyError}
              historyLoading={cart.historyLoading}
              navigateTo={navigateTo}
              orderHistory={cart.orderHistory}
            />
          ) : null}
        </main>

        <DemoFooter />
        <DemoProductModal
          isAdded={selectedProduct ? cart.lastAddedId === selectedProduct.id : false}
          isAddingToCart={cart.isAddingToCart}
          onAddToCart={(sourceElement) =>
            selectedProduct ? handleAddToCart(selectedProduct, sourceElement) : undefined
          }
          onClose={() => setSelectedProduct(null)}
          product={selectedProduct}
        />

        {toast ? (
          <div className="shopping-toast fixed bottom-6 left-1/2 z-[98] -translate-x-1/2 rounded-full border border-emerald-300/40 bg-emerald-500 px-5 py-3 text-sm font-semibold text-white shadow-[0_20px_50px_rgba(16,185,129,0.24)]">
            {toast}
          </div>
        ) : null}

        {flyToCart && !prefersLightMotion ? (
          <div
            className={`shopping-fly-token${flyToCart.active ? ' is-active' : ''}`}
            style={{
              '--fly-start-x': `${flyToCart.startX}px`,
              '--fly-start-y': `${flyToCart.startY}px`,
              '--fly-end-x': `${flyToCart.endX}px`,
              '--fly-end-y': `${flyToCart.endY}px`,
            }}
          >
            <div className={`shopping-fly-token-card bg-gradient-to-r ${flyToCart.accent}`}>
              <div className={`shopping-fly-token-orb ${flyToCart.orb}`} />
              <div className={`shopping-fly-token-panel ${flyToCart.panel}`} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DemoHeader({
  cartBounceKey,
  currentPage,
  isMenuOpen,
  itemCount,
  navigateBack,
  navigateTo,
  prefersLightMotion,
  setIsMenuOpen,
  setTheme,
  theme,
}) {
  const navItems = [
    { label: 'Home', to: 'home' },
    { label: 'Cart', to: 'cart' },
    { label: 'Checkout', to: 'checkout' },
    { label: 'History', to: 'history' },
  ];
  const logoSrc = theme === 'dark' ? lightLogo : darkLogo;
  const showBackButton = currentPage !== 'home';

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="mx-auto max-w-7xl px-4 pb-3 pt-4 sm:px-6 lg:px-8">
        <div className="rounded-[28px] border border-white/60 bg-white/72 px-4 py-4 shadow-[0_18px_55px_rgba(15,23,42,0.08)] backdrop-blur-2xl transition-colors duration-500 dark:border-white/10 dark:bg-stone-900/62 sm:px-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3 md:flex-none">
              {showBackButton ? (
                <button
                  aria-label="Go back"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-stone-200 bg-white/92 px-3 text-sm font-semibold text-stone-700 shadow-sm transition-all hover:-translate-y-0.5 hover:border-stone-300 hover:text-stone-950 dark:border-white/10 dark:bg-white/5 dark:text-stone-200 dark:hover:border-white/20 dark:hover:text-white"
                  onClick={navigateBack}
                  type="button"
                >
                  <ArrowLeftIcon />
                  <span className="hidden sm:inline">Back</span>
                </button>
              ) : null}

              <button className="min-w-0 bg-transparent p-0 text-left" onClick={() => navigateTo('home')} type="button">
                <div className="flex items-center gap-3">
                  <img
                    alt="ATA Code House logo"
                    className="h-11 w-11 shrink-0 rounded-2xl object-contain sm:h-12 sm:w-12"
                    src={logoSrc}
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-extrabold tracking-tight text-stone-950 dark:text-white sm:text-base">
                      ATA Code House
                    </p>
                    <p className="truncate text-[10px] font-semibold uppercase tracking-[0.28em] text-stone-500 dark:text-stone-400 sm:text-[11px]">
                      Build Better Digital Experiences
                    </p>
                  </div>
                </div>
              </button>
            </div>

            <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
              {navItems.map((item) => (
                <button
                  className={`transition-colors duration-300 ${
                    currentPage === item.to
                      ? 'text-stone-950 dark:text-white'
                      : 'text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-white'
                  }`}
                  key={item.to}
                  onClick={() => navigateTo(item.to)}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                aria-label="Toggle theme"
                className="grid h-11 w-11 place-items-center rounded-full border border-stone-200 bg-white text-stone-900 shadow-sm transition-colors dark:border-white/10 dark:bg-white/5 dark:text-white"
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                type="button"
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>

              <div
                className="transition-transform duration-500"
                key={cartBounceKey}
                style={
                  itemCount && !prefersLightMotion
                    ? { animation: 'shoppingCartPulse 1.9s ease-in-out infinite' }
                    : undefined
                }
              >
                <button
                  aria-label="Open cart"
                  className="relative grid h-11 w-11 place-items-center rounded-full bg-stone-950 text-white shadow-lg shadow-stone-900/15 transition-all hover:-translate-y-0.5 hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
                  data-cart-icon="true"
                  onClick={() => navigateTo('cart')}
                  type="button"
                >
                  <BagIcon />
                  {itemCount ? (
                    <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-stone-900">
                      {itemCount}
                    </span>
                  ) : null}
                </button>
              </div>

              <button
                aria-label="Toggle menu"
                className="grid h-11 w-11 place-items-center rounded-full border border-stone-200 bg-white text-stone-900 shadow-sm transition-colors md:hidden dark:border-white/10 dark:bg-white/5 dark:text-white"
                onClick={() => setIsMenuOpen((current) => !current)}
                type="button"
              >
                {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {isMenuOpen ? (
        <>
          <button
            aria-label="Close menu overlay"
            className="fixed inset-0 z-[55] bg-stone-950/30 backdrop-blur-sm md:hidden"
            onClick={() => setIsMenuOpen(false)}
            type="button"
          />
          <nav className="fixed left-4 right-4 top-24 z-[60] rounded-[28px] border border-white/60 bg-white/92 p-4 shadow-[0_24px_70px_rgba(15,23,42,0.18)] backdrop-blur-2xl md:hidden dark:border-white/10 dark:bg-stone-900/92">
            <div className="flex flex-col gap-3">
              {navItems.map((item) => (
                <button
                  className={`rounded-2xl px-4 py-3 text-left text-sm font-medium transition-colors ${
                    currentPage === item.to
                      ? 'bg-stone-950 text-white dark:bg-white dark:text-stone-900'
                      : 'text-stone-600 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-white/10'
                  }`}
                  key={item.to}
                  onClick={() => {
                    navigateTo(item.to);
                    setIsMenuOpen(false);
                  }}
                  type="button"
                >
                  {item.label}
                </button>
              ))}
            </div>
          </nav>
        </>
      ) : null}
    </header>
  );
}

function DemoHome({
  actionError,
  apiStatus,
  handleAddToCart,
  isAddingToCart,
  lastAddedId,
  products,
  productsError,
  productsLoading,
  setSelectedProduct,
}) {
  return (
    <div className="space-y-10 lg:space-y-14">
      <DemoCarousel />

      <section className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
              ATA Code House
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white sm:text-4xl">
              Premium commerce interactions
            </h2>
          </div>
          <div className="rounded-full border border-white/70 bg-white/70 px-4 py-2 text-sm text-stone-500 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/5 dark:text-stone-300">
            {apiStatus
              ? `Service Online • ${apiStatus.productCount} products`
              : 'Build Better Digital Experiences'}
          </div>
        </div>

        {productsError ? (
          <div className="rounded-[30px] border border-red-200 bg-red-50/80 p-5 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
            {productsError}
          </div>
        ) : null}

        {actionError ? (
          <div className="rounded-[30px] border border-red-200 bg-red-50/80 p-4 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
            {actionError}
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:gap-5 xl:grid-cols-4">
          {(productsLoading
            ? Array.from({ length: 8 }, (_, index) => ({ id: `loading-${index}`, loading: true }))
            : products
          ).map((product, index) =>
            product.loading ? (
              <div
                className="overflow-hidden rounded-[30px] border border-white/70 bg-white/80 p-3.5 shadow-soft backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04]"
                key={product.id}
              >
                <div className="aspect-[16/9] animate-pulse rounded-[26px] bg-gradient-to-r from-stone-200 via-stone-100 to-stone-200 dark:from-white/10 dark:via-white/5 dark:to-white/10" />
                <div className="mt-5 h-5 w-2/3 animate-pulse rounded-full bg-stone-200 dark:bg-white/10" />
                <div className="mt-3 h-4 w-1/3 animate-pulse rounded-full bg-stone-200 dark:bg-white/10" />
                <div className="mt-4 h-11 animate-pulse rounded-[20px] bg-stone-200 dark:bg-white/10" />
              </div>
            ) : (
              <DemoProductCard
                index={index}
                isAdded={lastAddedId === product.id}
                key={product.id}
                onOpenProduct={setSelectedProduct}
                onQuickAdd={handleAddToCart}
                product={product}
              />
            )
          )}
        </div>
      </section>

    </div>
  );
}

function DemoCart({ cartItems, navigateTo, removingIds, removeFromCart, totalPrice, updateQuantity }) {
  if (!cartItems.length) {
    return (
      <section className="mx-auto max-w-3xl rounded-[34px] border border-white/70 bg-white/82 p-8 text-center shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">Cart</p>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
          Your cart is ready for the next item
        </h1>
        <p className="mt-4 text-base leading-7 text-stone-500 dark:text-stone-400">
          Keep exploring the collection and the checkout summary will build here in real time.
        </p>
        <button
          className="mt-8 inline-flex rounded-full bg-stone-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
          onClick={() => navigateTo('home')}
          type="button"
        >
          Continue shopping
        </button>
      </section>
    );
  }

  return (
    <section className="grid gap-6 lg:grid-cols-[1.6fr,0.9fr]">
      <div className="space-y-4 rounded-[34px] border border-white/70 bg-white/82 p-5 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04] sm:p-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">Cart</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">Selected items</h1>
        </div>

        {cartItems.map((item) => (
          <article
            className={`shopping-cart-row flex flex-col gap-4 rounded-[28px] border border-stone-200/70 bg-stone-50/85 p-4 transition-colors dark:border-white/10 dark:bg-white/5 sm:flex-row sm:items-center sm:justify-between${
              removingIds.includes(item.id) ? ' is-removing' : ''
            }`}
            key={item.id}
          >
            <div className="flex items-center gap-4">
              <div className={`relative h-16 w-28 overflow-hidden rounded-[22px] bg-gradient-to-r ${item.accent}`}>
                <div className={`absolute left-3 top-3 h-6 w-6 rounded-full ${item.orb} opacity-70 blur-sm`} />
                <div className={`absolute bottom-2 right-3 h-8 w-8 rounded-2xl ${item.panel} border border-white/25 backdrop-blur-sm dark:border-white/10`} />
              </div>
              <div className="min-w-0">
                <h2 className="truncate whitespace-nowrap text-lg font-semibold text-stone-900 dark:text-white">
                  {item.title}
                </h2>
                <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">{formatMMK(item.price)}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center rounded-full border border-stone-200 bg-white p-1 dark:border-white/10 dark:bg-transparent">
                <button
                  className="h-9 w-9 rounded-full text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-white/10"
                  disabled={removingIds.includes(item.id)}
                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                  type="button"
                >
                  -
                </button>
                <span className="min-w-10 text-center text-sm font-semibold">{item.quantity}</span>
                <button
                  className="h-9 w-9 rounded-full text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-200 dark:hover:bg-white/10"
                  disabled={removingIds.includes(item.id)}
                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                  type="button"
                >
                  +
                </button>
              </div>

              <button
                className="rounded-full border border-stone-200 px-4 py-2 text-sm font-medium text-stone-500 transition-colors hover:border-red-300 hover:text-red-500 dark:border-white/10 dark:text-stone-300 dark:hover:border-red-400/40 dark:hover:text-red-300"
                disabled={removingIds.includes(item.id)}
                onClick={() => removeFromCart(item.id)}
                type="button"
              >
                Remove
              </button>
            </div>
          </article>
        ))}
      </div>

      <aside className="h-fit rounded-[34px] border border-white/70 bg-white/82 p-6 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">Total</p>
        <h2 className="mt-3 text-4xl font-semibold tracking-tight text-stone-950 dark:text-white">
          {formatMMK(totalPrice)}
        </h2>
        <p className="mt-3 text-sm leading-7 text-stone-500 dark:text-stone-400">
          Clear quantity controls, soft contrast, and calm spacing keep the cart experience polished.
        </p>
        <button
          className="mt-8 inline-flex w-full justify-center rounded-full bg-stone-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
          onClick={() => navigateTo('checkout')}
          type="button"
        >
          Checkout
        </button>
      </aside>
    </section>
  );
}

function DemoCheckout({ cartItems, isPlacingOrder, navigateTo, placeOrder, totalPrice }) {
  const [form, setForm] = useState({ name: '', address: '', phone: '' });
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    try {
      await placeOrder(form);
      setShowSuccess(true);
      setForm({ name: '', address: '', phone: '' });

      window.setTimeout(() => {
        setShowSuccess(false);
        navigateTo();
      }, 1800);
    } catch (error) {
      setSubmitError(error.message || 'Unable to place order right now.');
    }
  };

  return (
    <section className="grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
      <form
        className="rounded-[34px] border border-white/70 bg-white/82 p-6 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]"
        onSubmit={handleSubmit}
      >
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
          Checkout
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
          Delivery details
        </h1>

        <div className="mt-8 grid gap-5">
          {[
            { label: 'Name', name: 'name', type: 'text' },
            { label: 'Address', name: 'address', type: 'text' },
            { label: 'Phone', name: 'phone', type: 'tel' },
          ].map((field) => (
            <label
              className="grid gap-2 text-sm font-medium text-stone-600 dark:text-stone-300"
              key={field.name}
            >
              {field.label}
              <input
                className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-stone-900 outline-none transition focus:border-amber-400 dark:border-white/10 dark:bg-white/5 dark:text-white"
                name={field.name}
                onChange={handleChange}
                required
                type={field.type}
                value={form[field.name]}
              />
            </label>
          ))}
        </div>

        {submitError ? (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200">
            {submitError}
          </div>
        ) : null}

        <button
          className="mt-8 inline-flex rounded-full bg-stone-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
          disabled={!cartItems.length || isPlacingOrder}
          type="submit"
        >
          {isPlacingOrder ? 'Processing...' : 'Place Order'}
        </button>
      </form>

      <aside className="relative rounded-[34px] border border-white/70 bg-white/82 p-6 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
          Summary
        </p>
        <h2 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
          {formatMMK(totalPrice)}
        </h2>

        <div className="mt-6 space-y-3">
          {cartItems.length ? (
            cartItems.map((item) => (
              <div
                className="flex items-center gap-4 rounded-2xl border border-stone-200/70 bg-stone-50/85 px-4 py-3 dark:border-white/10 dark:bg-white/5"
                key={item.id}
              >
                <div className={`relative h-14 w-24 shrink-0 overflow-hidden rounded-[16px] bg-gradient-to-r ${item.accent}`}>
                  <div className={`absolute left-2 top-2 h-4 w-4 rounded-full ${item.orb} opacity-80 blur-sm`} />
                  <div className={`absolute bottom-2 right-2 h-6 w-6 rounded-xl ${item.panel} border border-white/25 backdrop-blur-sm dark:border-white/10`} />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block truncate whitespace-nowrap text-sm font-medium text-stone-700 dark:text-stone-200">
                    {item.title}
                  </span>
                  <span className="mt-1 block text-sm text-stone-500 dark:text-stone-400">
                    {item.quantity} x {formatMMK(item.price)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-stone-300 px-4 py-6 text-sm text-stone-500 dark:border-white/10 dark:text-stone-400">
              Cart is empty
            </div>
          )}
        </div>

        {showSuccess ? (
          <div className="absolute inset-4 grid place-items-center rounded-[28px] bg-stone-950/92 text-center text-white backdrop-blur">
            <div>
              <div className="mx-auto mb-4 grid h-20 w-20 place-items-center rounded-full bg-white text-2xl text-stone-900">
                <svg
                  className="h-8 w-8"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  viewBox="0 0 24 24"
                >
                  <path d="m5 12 4.5 4.5L19 7" />
                </svg>
              </div>
              <p className="text-sm uppercase tracking-[0.32em] text-white/60">Success</p>
              <h3 className="mt-2 text-2xl font-semibold">Order placed</h3>
            </div>
          </div>
        ) : null}
      </aside>
    </section>
  );
}

function DemoHistory({ historyError, historyLoading, navigateTo, orderHistory }) {
  if (historyLoading) {
    return (
      <section className="space-y-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
            History
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
            Order history
          </h1>
        </div>
        <div className="grid gap-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]"
              key={index}
            >
              <div className="h-6 w-40 animate-pulse rounded-full bg-stone-200 dark:bg-white/10" />
              <div className="mt-4 h-9 w-28 animate-pulse rounded-full bg-stone-200 dark:bg-white/10" />
              <div className="mt-6 grid gap-3">
                <div className="h-20 animate-pulse rounded-2xl bg-stone-200 dark:bg-white/10" />
                <div className="h-20 animate-pulse rounded-2xl bg-stone-200 dark:bg-white/10" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (historyError) {
    return (
      <section className="mx-auto max-w-3xl rounded-[34px] border border-red-200 bg-red-50/80 p-8 text-center shadow-soft backdrop-blur-2xl dark:border-red-500/20 dark:bg-red-500/10">
        <p className="text-sm text-red-600 dark:text-red-200">{historyError}</p>
      </section>
    );
  }

  if (!orderHistory.length) {
    return (
      <section className="mx-auto max-w-3xl rounded-[34px] border border-white/70 bg-white/82 p-8 text-center shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
          History
        </p>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
          No orders yet
        </h1>
        <p className="mt-4 text-base leading-7 text-stone-500 dark:text-stone-400">
          Completed checkouts will appear here with totals, timestamps, and product previews.
        </p>
        <button
          className="mt-8 inline-flex rounded-full bg-stone-950 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200"
          onClick={() => navigateTo('home')}
          type="button"
        >
          Browse products
        </button>
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
          History
        </p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight text-stone-950 dark:text-white">
          Order history
        </h1>
      </div>

      <div className="grid gap-4">
        {orderHistory.map((order) => (
          <article
            className="rounded-[30px] border border-white/70 bg-white/82 p-6 shadow-soft backdrop-blur-2xl dark:border-white/10 dark:bg-white/[0.04]"
            key={order.id}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
                  {order.id}
                </p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-stone-950 dark:text-white">
                  {formatMMK(order.total)}
                </h2>
                <p className="mt-2 text-sm text-stone-500 dark:text-stone-400">
                  {new Date(order.createdAt).toLocaleString()}
                </p>
                <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
                  {order.estimatedDelivery}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-emerald-200/80 bg-emerald-50/90 px-4 py-2 text-sm font-medium text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                  {order.status}
                </div>
                <div className="rounded-full border border-stone-200/80 bg-stone-50/90 px-4 py-2 text-sm font-medium text-stone-600 dark:border-white/10 dark:bg-white/5 dark:text-stone-300">
                  {order.items.reduce((sum, item) => sum + item.quantity, 0)} items
                </div>
              </div>
            </div>

            <div className="mt-5 grid gap-3">
              {order.items.map((item) => (
                <div
                  className="flex items-center gap-4 rounded-2xl border border-stone-200/70 bg-stone-50/85 px-4 py-3 dark:border-white/10 dark:bg-white/5"
                  key={`${order.id}-${item.id}`}
                >
                  <div className={`relative h-16 w-24 shrink-0 overflow-hidden rounded-[18px] bg-gradient-to-r ${item.accent}`}>
                    <div className={`absolute left-2 top-2 h-5 w-5 rounded-full ${item.orb} opacity-80 blur-sm`} />
                    <div className={`absolute bottom-2 right-2 h-7 w-7 rounded-2xl ${item.panel} border border-white/25 backdrop-blur-sm dark:border-white/10`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate whitespace-nowrap text-sm font-medium text-stone-700 dark:text-stone-200">
                      {item.title}
                    </p>
                    <p className="mt-1 text-sm text-stone-500 dark:text-stone-400">
                      {item.quantity} x {formatMMK(item.price)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function DemoCarousel() {
  const slides = [
    {
      id: 1,
      gradient: 'from-[#0f172a] via-[#13223d] to-[#0f766e]',
      shapeOne: 'bg-cyan-300/20',
      shapeTwo: 'bg-white/10',
      shapeThree: 'bg-amber-200/15',
    },
    {
      id: 2,
      gradient: 'from-[#111827] via-[#3b0764] to-[#9d174d]',
      shapeOne: 'bg-fuchsia-300/20',
      shapeTwo: 'bg-white/10',
      shapeThree: 'bg-sky-200/15',
    },
    {
      id: 3,
      gradient: 'from-[#111827] via-[#7c2d12] to-[#b45309]',
      shapeOne: 'bg-amber-200/20',
      shapeTwo: 'bg-white/10',
      shapeThree: 'bg-rose-200/15',
    },
  ];
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = slides[activeIndex];

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length);
    }, 3800);

    return () => window.clearInterval(interval);
  }, [slides.length]);

  return (
    <section className="relative overflow-hidden rounded-[38px] border border-white/10 shadow-[0_28px_120px_rgba(15,23,42,0.2)]">
      <div className={`relative aspect-[16/9] w-full overflow-hidden bg-gradient-to-br ${activeSlide.gradient} sm:aspect-[16/8] lg:aspect-[16/6.2]`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.16),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(255,255,255,0.12),transparent_24%)]" />
        <div className="shopping-banner-slide absolute inset-0 transition-all duration-700" key={activeSlide.id}>
          <div className={`absolute right-[10%] top-[15%] h-[24%] w-[24%] rounded-full ${activeSlide.shapeOne} blur-md`} style={{ animation: 'shoppingFloatA 7.5s ease-in-out infinite' }} />
          <div className={`absolute bottom-[14%] right-[11%] h-[34%] w-[26%] rounded-[48px] ${activeSlide.shapeTwo} backdrop-blur-lg`} style={{ animation: 'shoppingFloatB 8.5s ease-in-out infinite' }} />
          <div className={`absolute bottom-[-6%] left-[7%] h-[38%] w-[28%] rounded-full border border-white/10 ${activeSlide.shapeThree}`} style={{ animation: 'shoppingFloatC 8.8s ease-in-out infinite' }} />
          <div className="absolute left-[32%] top-[18%] h-[22%] w-[16%] rounded-[36px] border border-white/15 bg-white/10 backdrop-blur-md" style={{ animation: 'shoppingFloatD 9.2s ease-in-out infinite' }} />
          <div className="absolute bottom-[16%] left-[48%] h-[18%] w-[12%] rounded-full border border-white/15 bg-white/10" style={{ animation: 'shoppingFloatE 7.2s ease-in-out infinite' }} />
        </div>

        <div className="absolute inset-x-0 bottom-4 z-10 flex items-center justify-center gap-3 sm:bottom-5">
          {slides.map((slide, index) => (
            <button
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2.5 rounded-full transition-all ${
                index === activeIndex ? 'w-14 bg-white' : 'w-2.5 bg-white/35 hover:bg-white/55'
              }`}
              key={slide.id}
              onClick={() => setActiveIndex(index)}
              type="button"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function DemoProductCard({ index, isAdded, onOpenProduct, onQuickAdd, product }) {
  return (
    <article
      className="group relative overflow-hidden rounded-[30px] border border-white/70 bg-white/80 p-3.5 shadow-soft backdrop-blur-xl transition-all duration-300 hover:-translate-y-1.5 dark:border-white/10 dark:bg-white/[0.04]"
      style={{ animationDelay: `${index * 0.03}s` }}
    >
      <button
        aria-label={`Open ${product.title}`}
        className="w-full text-left"
        onClick={() => onOpenProduct(product)}
        type="button"
      >
        <div className="absolute inset-x-5 top-4 h-20 rounded-full bg-gradient-to-r from-white/35 to-transparent blur-2xl dark:from-white/10" />
        <div className="relative">
          <SkeletonArt product={product} />
          {product.badge ? (
            <div
              className={`absolute left-3 top-3 z-10 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.24em] backdrop-blur-sm ${product.badge.className}`}
            >
              {product.badge.text}
            </div>
          ) : null}
        </div>

        <div className="px-1 pb-1 pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="truncate whitespace-nowrap text-base font-semibold tracking-tight text-stone-950 dark:text-white sm:text-lg">
                {product.title}
              </h3>
              <div className="mt-2 inline-flex rounded-full border border-emerald-500/30 bg-transparent px-4 py-1.5 text-sm font-bold tracking-tight text-emerald-600 shadow-sm dark:border-emerald-400/40 dark:text-emerald-400">
                {formatMMK(product.price)}
              </div>
            </div>
          </div>
        </div>
      </button>

      <button
        className={`shopping-add-cta mt-3 flex w-full items-center justify-center rounded-[20px] px-4 py-3 text-sm font-semibold transition-all duration-200 ${
          isAdded
            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20'
            : 'bg-stone-950 text-white shadow-lg shadow-stone-900/15 hover:bg-stone-800 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200'
        }`}
        onClick={(event) => onQuickAdd(product, event.currentTarget)}
        type="button"
      >
        <span className="flex items-center gap-2">
          {isAdded ? <CheckIcon /> : <PlusIcon />}
          {isAdded ? 'Added' : 'Add to Cart'}
        </span>
      </button>
    </article>
  );
}

function DemoProductModal({ isAdded, isAddingToCart, onAddToCart, onClose, product }) {
  if (!product) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-[120] overflow-hidden bg-stone-950/45 px-4 py-24 backdrop-blur-md sm:px-6 sm:py-12"
      onClick={onClose}
      role="presentation"
    >
      <div className="flex min-h-full items-center justify-center">
        <div
          className="relative my-0 w-full max-w-4xl overflow-hidden rounded-[32px] border border-white/60 bg-white/88 shadow-[0_30px_120px_rgba(15,23,42,0.22)] backdrop-blur-2xl dark:border-white/10 dark:bg-[#111827]/85"
          onClick={(event) => event.stopPropagation()}
          style={{ maxHeight: 'min(88vh, 760px)' }}
        >
          <button
            aria-label="Close product details"
            className="absolute right-5 top-5 z-20 grid h-10 w-10 place-items-center rounded-full border border-stone-200/80 bg-white/90 text-stone-700 transition hover:text-stone-950 dark:border-white/10 dark:bg-white/10 dark:text-stone-200 dark:hover:text-white"
            onClick={onClose}
            type="button"
          >
            <CloseIcon />
          </button>

          <div className="grid h-full gap-0 md:grid-cols-[1.02fr,0.98fr]">
            <div className={`relative flex min-h-[220px] items-center justify-center overflow-hidden bg-gradient-to-r ${product.accent} p-5 sm:min-h-[260px] sm:p-6 md:min-h-0 md:p-8`}>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.26),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(15,23,42,0.18),transparent_32%)]" />
              <div className="relative w-full max-w-[560px]">
                <SkeletonArt
                  className="w-full rounded-[30px] border border-white/40 shadow-[0_28px_60px_rgba(15,23,42,0.2)]"
                  product={product}
                />
              </div>
              {product.badge ? (
                <div
                  className={`absolute left-5 top-5 z-10 rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.28em] backdrop-blur-sm sm:left-6 sm:top-6 ${product.badge.className}`}
                >
                  {product.badge.text}
                </div>
              ) : null}
            </div>

            <div className="flex flex-col justify-center p-5 sm:p-6 md:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-stone-500 dark:text-stone-400">
                Product
              </p>
              <h2 className="mt-3 text-2xl font-semibold tracking-tight text-stone-950 dark:text-white sm:text-3xl md:text-4xl">
                {product.title}
              </h2>
              <p className="mt-3 text-xl font-semibold text-stone-900 dark:text-stone-100 sm:text-2xl">
                {formatMMK(product.price)}
              </p>
              <p className="mt-4 max-w-md text-sm leading-6 text-stone-600 dark:text-stone-300 sm:text-base sm:leading-7">
                {product.description}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  className={`rounded-full px-6 py-3 text-sm font-semibold transition-all ${
                    isAdded
                      ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/25'
                      : 'bg-stone-950 text-white shadow-lg shadow-stone-900/20 hover:bg-stone-800 disabled:opacity-60 dark:bg-white dark:text-stone-900 dark:hover:bg-stone-200'
                  }`}
                  disabled={isAddingToCart}
                  onClick={(event) => onAddToCart(event.currentTarget)}
                  type="button"
                >
                  <span className="flex items-center gap-2">
                    {isAdded ? <CheckIcon /> : <PlusIcon />}
                    {isAdded ? 'Added to Cart' : isAddingToCart ? 'Adding...' : 'Add to Cart'}
                  </span>
                </button>

                <button
                  className="rounded-full border border-stone-200/80 px-5 py-3 text-sm font-semibold text-stone-600 transition hover:border-stone-300 hover:text-stone-900 dark:border-white/10 dark:text-stone-300 dark:hover:text-white"
                  onClick={onClose}
                  type="button"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonArt({ className = '', product }) {
  return (
    <div className={`relative aspect-[16/9] overflow-hidden rounded-[26px] bg-gradient-to-r ${product.accent} ${className}`}>
      <div className="absolute inset-y-0 left-[-20%] w-2/3 bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/20" style={{ animation: 'shoppingShine 1.9s linear infinite' }} />
      <div className={`absolute left-[8%] top-[18%] h-[28%] w-[18%] rounded-full ${product.orb} opacity-80 blur-sm`} style={{ animation: 'shoppingFloatD 8s ease-in-out infinite' }} />
      <div className={`absolute right-[8%] top-[20%] h-[46%] w-[28%] rounded-[30px] ${product.panel} border border-white/30 backdrop-blur-md dark:border-white/10`} style={{ animation: 'shoppingFloatB 7s ease-in-out infinite' }} />
      <div className="absolute bottom-[14%] left-[24%] h-[18%] w-[32%] rounded-full border border-white/25 bg-white/15 backdrop-blur-sm dark:border-white/10" style={{ animation: 'shoppingFloatC 9s ease-in-out infinite' }} />
      <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-black/10 to-transparent" />
    </div>
  );
}

function DemoFooter() {
  return (
    <footer className="relative z-10 border-t border-stone-200/70 py-8 transition-colors duration-500 dark:border-white/10">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8 dark:text-stone-400">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.32em]">ATA Code House</p>
          <p className="mt-1">Shopping demo with connected mock backend flow</p>
        </div>
        <p>React, Tailwind, motion-style UI, local mock service</p>
      </div>
    </footer>
  );
}

function useShoppingCart() {
  const [cartItems, setCartItems] = usePersistentState('ata-code-house-cart', []);
  const [orderHistory, setOrderHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState('');
  const [lastAddedId, setLastAddedId] = useState(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [removingIds, setRemovingIds] = useState([]);

  useEffect(() => {
    let active = true;

    async function loadOrders() {
      setHistoryLoading(true);
      setHistoryError('');

      try {
        const orders = await fetchOrders();
        if (active) {
          setOrderHistory(orders);
        }
      } catch (error) {
        if (active) {
          setHistoryError(error.message || 'Unable to load order history.');
        }
      } finally {
        if (active) {
          setHistoryLoading(false);
        }
      }
    }

    loadOrders();
    return () => {
      active = false;
    };
  }, []);

  const addToCart = async (product) => {
    setIsAddingToCart(true);
    setLastAddedId(product.id);
    setCartItems((current) => {
      const existing = current.find((item) => item.id === product.id);
      if (existing) {
        return current.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }

      return [...current, { ...product, quantity: 1 }];
    });

    try {
      await postCartItem(product);
      window.setTimeout(() => {
        setLastAddedId((current) => (current === product.id ? null : current));
      }, 1400);
    } catch (error) {
      setCartItems((current) => {
        const existing = current.find((item) => item.id === product.id);
        if (!existing) {
          return current;
        }

        if (existing.quantity === 1) {
          return current.filter((item) => item.id !== product.id);
        }

        return current.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity - 1 } : item
        );
      });
      setLastAddedId((current) => (current === product.id ? null : current));
      throw error;
    } finally {
      setIsAddingToCart(false);
    }
  };

  const removeFromCart = (productId) => {
    setRemovingIds((current) => (current.includes(productId) ? current : [...current, productId]));
    window.setTimeout(() => {
      setCartItems((current) => current.filter((item) => item.id !== productId));
      setRemovingIds((current) => current.filter((itemId) => itemId !== productId));
    }, 240);
  };

  const updateQuantity = (productId, nextQuantity) => {
    if (nextQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCartItems((current) =>
      current.map((item) =>
        item.id === productId ? { ...item, quantity: nextQuantity } : item
      )
    );
  };

  const placeOrder = async (customer) => {
    setIsPlacingOrder(true);

    try {
      const createdOrder = await createOrder({
        customer,
        items: cartItems,
        total: cartItems.reduce((total, item) => total + item.price * item.quantity, 0),
      });

      setOrderHistory((current) => [createdOrder, ...current]);
      setCartItems([]);
      return createdOrder;
    } finally {
      setIsPlacingOrder(false);
    }
  };

  return {
    addToCart,
    cartItems,
    historyError,
    historyLoading,
    isAddingToCart,
    isPlacingOrder,
    itemCount: cartItems.reduce((total, item) => total + item.quantity, 0),
    lastAddedId,
    orderHistory,
    placeOrder,
    removingIds,
    removeFromCart,
    totalPrice: cartItems.reduce((total, item) => total + item.price * item.quantity, 0),
    updateQuantity,
  };
}

function usePersistentState(key, initialValue) {
  const [value, setValue] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);

  return [value, setValue];
}

function MoonIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.2M12 19.3v2.2M4.7 4.7l1.6 1.6M17.7 17.7l1.6 1.6M2.5 12h2.2M19.3 12h2.2M4.7 19.3l1.6-1.6M17.7 6.3l1.6-1.6" />
    </svg>
  );
}

function ArrowLeftIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.9" viewBox="0 0 24 24">
      <path d="m10 6-6 6 6 6" />
      <path d="M4 12h16" />
    </svg>
  );
}

function BagIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M3.5 6.5h2.2l2 9.4a1.8 1.8 0 0 0 1.8 1.4h7.8a1.8 1.8 0 0 0 1.8-1.4l1.2-6.9H7.2" />
      <circle cx="10" cy="20" fill="currentColor" r="1.2" stroke="none" />
      <circle cx="18" cy="20" fill="currentColor" r="1.2" stroke="none" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="m5 12 4.5 4.5L19 7" />
    </svg>
  );
}

export default ShoppingAppShowcase;
